import {
  AllPointOfInterestTemplateDocument,
  GetVersionsDocument,
  PointOfInterest,
  PointOfInterestTemplateTypeEnum,
  useCreatePointOfInterestTemplateMutation,
  useDeletePointOfInterestTemplateMutation,
  useGetAllPoiIconsForTemplateBuilderQuery,
  usePointOfInterestsCountLazyQuery,
  usePointOfInterestsLazyQuery,
  useUpdatePointOfInterestTemplateMutation,
} from '@/__generated__/graphql';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BasicTabs } from '../../../shared/BasicTabs';
import Button from '../../../shared/Button';
import { PageLoader } from '../../../shared/PageLoader';
import { selectedPOIType, selectedTemplateId } from '@/components/TemplateBuilder/state';
import { useSnackBarMessage } from '../../utils';
import { PoiFieldsPanel } from './PoiFieldsPanel';
import { FloatingLine, poiTabStyles } from './styles';
import { PoiMarkerLabelPanel } from './PoiMarkerLabelPanel';
import { PoiTemplateEngineKitPanel } from './PoiTemplateEngineKitPanel';
import { PoiBlisterFieldsPanel } from './PoiBlisterFieldsPanel';
import { useFeatureFlag } from '@/hooks';
import { upperSnakeCase } from '@/utils';

export const PoiTemplateForm = ({
  emptyForm,
  handleEmptyForm,
  structureId,
}: {
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
  structureId: string;
}) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [selectedPoiCount, setSelectedPoiCount] = useState<number>(0);
  const [examplePoi, setExamplePoi] = useState<PointOfInterest | undefined>(undefined);
  const [allPoiIcons, setAllPoiIcons] = useState<string[]>([]);

  const selectedAssemblyTemplateId = useAtomValue(selectedTemplateId);

  const workInProgress = useFeatureFlag('work-in-progress');

  const { data: poiIconsData } = useGetAllPoiIconsForTemplateBuilderQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const allIcons = poiIconsData?.getAllPoiIcons;
    if (allIcons && allIcons?.length > 0) {
      const iconNames = allIcons.filter((icon) => icon !== null);
      setAllPoiIcons(iconNames);
    }
  }, [poiIconsData?.getAllPoiIcons]);

  const [fetchPoiCount] = usePointOfInterestsCountLazyQuery();
  const [fetchPois] = usePointOfInterestsLazyQuery();

  useEffect(() => {
    fetchPoiCount({
      variables: {
        input: {
          structureId,
          type:  upperSnakeCase(selectedType?.key) ??selectedType?.name?.toUpperCase(),
        },
      },
      fetchPolicy: 'cache-first',
    }).then(({ data }) => {
      if (data?.pointOfInterestsCount?.count !== undefined) {
        setSelectedPoiCount(data.pointOfInterestsCount.count);
      }
    });
  }, [fetchPoiCount, selectedType?.key, selectedType?.name, structureId]);

  useEffect(() => {
    fetchPois({
      variables: {
        input: {
          structureId,
          type: upperSnakeCase(selectedType?.key) ??selectedType?.name?.toUpperCase(),
        },
        pageInfo: {
          limit: 1,
        },
      },
      fetchPolicy: 'cache-first',
    }).then(({ data }) => {
      if (data?.pointOfInterests) {
        setExamplePoi(data.pointOfInterests.at(0) as PointOfInterest);
      } else {
        setExamplePoi(undefined);
      }
    });
  }, [fetchPoiCount, fetchPois, selectedType?.key, selectedType?.name, structureId]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [updatePoiTemplate] = useUpdatePointOfInterestTemplateMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument, GetVersionsDocument],
    onCompleted: (response) => {
      if (!response?.updatePointOfInterestTemplate) {
        showSnackBar('An error occurred while updating template', 'error');
        return;
      }
      showSnackBar('Template saved successfully', 'success');
      if (selectedType) {
        setSelectedType({
          ...response?.updatePointOfInterestTemplate,
        });
      }
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const [createPointOfInterestTemplate] = useCreatePointOfInterestTemplateMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument, GetVersionsDocument],
    onCompleted: (response) => {
      if (!response?.createPointOfInterestTemplate) {
        showSnackBar('An error occurred while creating template', 'error');
        return;
      }
      showSnackBar('Template created successfully', 'success');
      if (selectedType) {
        setSelectedType({
          ...response?.createPointOfInterestTemplate,
        });
      }
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const handleSaveTemplate = useCallback(() => {
    if (selectedType?.id) {
      const existingNames = new Set<string>();
      const uniqueFields = [];
      const duplicateFields = [];

      for (const field of selectedType?.fields ?? []) {
        if (field.name && existingNames.has(field.name)) {
          duplicateFields.push(field);
        } else {
          if (field.name) existingNames.add(field.name);
          uniqueFields.push(field);
        }
      }

      if (duplicateFields.length > 0) {
        showSnackBar('Remove duplicate fields to save template.', 'error');
        return;
      }

      // Clean condition tree recursively
       
      const cleanConditionTree = (node: any): any => {
        if (!node) return node;

        const cleanedNode = { ...node };
        delete cleanedNode.__typename;
        delete cleanedNode.id;

        // Remove null enum values
        if (cleanedNode.nodeType === null) delete cleanedNode.nodeType;
        if (cleanedNode.operator === null) delete cleanedNode.operator;
        if (cleanedNode.logicOperator === null) delete cleanedNode.logicOperator;
        if (cleanedNode.inputVariable === null) delete cleanedNode.inputVariable;
        if (cleanedNode.value === null) delete cleanedNode.value;

        // Recursively clean nested conditions
        if (cleanedNode.conditions && Array.isArray(cleanedNode.conditions)) {
          cleanedNode.conditions = cleanedNode.conditions.map(cleanConditionTree);
        }

        return cleanedNode;
      };

      // Clean field function
       
      const cleanField = (field: any) => {
        const cleaned = { ...field };
        delete cleaned.__typename;

        if (cleaned.options && Array.isArray(cleaned.options)) {
          cleaned.options = cleaned.options
            .filter((option: string) => option && typeof option === 'string')
            .map((option: string) => option.trim());
        }

        // Generate key if missing
        if (!cleaned.key && cleaned.name) {
          cleaned.key = cleaned.name.replace(/\s+/g, '_').toLowerCase();
        }

        // Clean displayConfig
        if (cleaned.displayConfig?.__typename) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { __typename, ...rest } = cleaned.displayConfig;
          if (rest.color) {
             
            rest.color = rest.color.map((color: any) =>
              JSON.parse(JSON.stringify(color), (key, value) => {
                return key === '__typename' ? undefined : value;
              })
            );
          }
          // Clean inputAdorment inside displayConfig
          if (rest.inputAdorment?.__typename) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { __typename: _typename, ...adormentRest } = rest.inputAdorment;
            rest.inputAdorment = adormentRest;
          }
          cleaned.displayConfig = rest;
        }

        // Clean optionsData
        if (cleaned.optionsData) {
           
          cleaned.optionsData = cleaned.optionsData.map((option: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { __typename, ...rest } = option;
            return {
              ...rest,
              weight: option.weight ?? 0,
            };
          });
        }

        // Clean inputAdorment
        if (cleaned.inputAdorment?.__typename) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { __typename, ...rest } = cleaned.inputAdorment;
          cleaned.inputAdorment = rest;
        }

        // Clean logicRule
        if (cleaned.logicRule) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { __typename, ...logicRuleRest } = cleaned.logicRule;

          // Set default evaluationStrategy if null or undefined (required field)
          if (!logicRuleRest.evaluationStrategy) {
            logicRuleRest.evaluationStrategy = 'FIRST_MATCH';
          }
          // Remove null values from optional fields
          if (logicRuleRest.defaultValue === null) delete logicRuleRest.defaultValue;
          if (logicRuleRest.description === null) delete logicRuleRest.description;

          cleaned.logicRule = {
            ...logicRuleRest,
             
            variables: logicRuleRest.variables?.map((v: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { __typename, ...varRest } = v;
              // Remove null values from variables
              if (varRest.templateType === null) delete varRest.templateType;
              if (varRest.isBlisterField === null) delete varRest.isBlisterField;
              return varRest;
            }),
             
            conditionalMappings: logicRuleRest.conditionalMappings?.map((m: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { __typename, id, ...mappingRest } = m;
              // Remove null enum values
              if (mappingRest.combineOperator === null) delete mappingRest.combineOperator;
              return {
                ...mappingRest,
                conditionTree: cleanConditionTree(mappingRest.conditionTree),
              };
            }),
          };
        }

        // Clean calculatedBy
        if (cleaned.calculatedBy) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { __typename, ...calculatedByRest } = cleaned.calculatedBy;

          // Set default evaluationStrategy if null or undefined (required field)
          if (!calculatedByRest.evaluationStrategy) {
            calculatedByRest.evaluationStrategy = 'FIRST_MATCH';
          }
          // Remove null values from optional fields
          if (calculatedByRest.defaultValue === null) delete calculatedByRest.defaultValue;
          if (calculatedByRest.description === null) delete calculatedByRest.description;

          cleaned.calculatedBy = {
            ...calculatedByRest,
             
            variables: calculatedByRest.variables?.map((v: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { __typename, ...varRest } = v;
              // Remove null values from variables
              if (varRest.templateType === null) delete varRest.templateType;
              if (varRest.isBlisterField === null) delete varRest.isBlisterField;
              return varRest;
            }),
             
            conditionalMappings: calculatedByRest.conditionalMappings?.map((m: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { __typename, id, ...mappingRest } = m;
              // Remove null enum values
              if (mappingRest.combineOperator === null) delete mappingRest.combineOperator;
              return {
                ...mappingRest,
                conditionTree: cleanConditionTree(mappingRest.conditionTree),
              };
            }),
          };
        }

        return cleaned;
      };

      updatePoiTemplate({
        variables: {
          input: {
            name: selectedType?.name ?? '',
            type: selectedType?.type ?? PointOfInterestTemplateTypeEnum.PointOfInterest,
            pointOfInterestTemplateId: selectedType?.id,
            accentColor: selectedType?.accentColor,
            icon: JSON.parse(JSON.stringify(selectedType?.icon), (key, value) => {
              return key === '__typename' ? undefined : value;
            }),
            fields: selectedType?.fields ? selectedType?.fields.map(cleanField) : [],
            lastModifiedFor: 'Update POI Template',
            ...(selectedType?.markerLabelConfig
              ? {
                markerLabelConfig: {
                  type: selectedType?.markerLabelConfig?.type,
                  hideName: selectedType?.markerLabelConfig?.hideName ?? false,
                  headerField: selectedType?.markerLabelConfig?.headerField
                    ? {
                      key: selectedType?.markerLabelConfig?.headerField?.key,
                      type: selectedType?.markerLabelConfig?.headerField?.type,
                    }
                    : undefined,
                  fields: selectedType?.markerLabelConfig?.fields.map((field) => ({
                    key: field.key,
                    type: field.type,
                  })),
                },
              }
              : {}),
            ...(selectedType?.blisterFieldsConfig
              ? {
                blisterFieldsConfig: JSON.parse(
                  JSON.stringify(selectedType?.blisterFieldsConfig),
                  (key, value) => {
                    return key === '__typename' ? undefined : value;
                  }
                ),
              }
              : {}),
          },
        },
      });
    }
  }, [
    showSnackBar,
    selectedType?.id,
    selectedType?.name,
    selectedType?.type,
    selectedType?.accentColor,
    selectedType?.icon,
    selectedType?.fields,
    selectedType?.markerLabelConfig,
    selectedType?.blisterFieldsConfig,
    updatePoiTemplate,
  ]);

  const handleCreateTemplate = useCallback(() => {
    if (!selectedAssemblyTemplateId) {
      showSnackBar('Assembly template is not selected', 'error');
      return;
    }

    if (!selectedType?.name) {
      showSnackBar('Template name is required', 'error');
      return;
    }

    createPointOfInterestTemplate({
      variables: {
        input: {
          accentColor: selectedType?.accentColor,
          assemblyTemplateId: selectedAssemblyTemplateId || '',
          icon: JSON.parse(JSON.stringify(selectedType?.icon), (key, value) => {
            return key === '__typename' ? undefined : value;
          }),
          fields: selectedType?.fields
            ? selectedType?.fields.map((field) =>
              JSON.parse(JSON.stringify(field), (key, value) => {
                return key === '__typename' ? undefined : value;
              })
            )
            : [],
          name: selectedType?.name ?? '',
          type: selectedType?.type ?? PointOfInterestTemplateTypeEnum.PointOfInterest,
          structureId: structureId,
          lastModifiedFor: 'Create POI Template',
          ...(selectedType?.blisterFieldsConfig
            ? {
              blisterFieldsConfig: JSON.parse(
                JSON.stringify(selectedType?.blisterFieldsConfig),
                (key, value) => {
                  return key === '__typename' ? undefined : value;
                }
              ),
            }
            : {}),
        },
      },
    });
  }, [
    selectedAssemblyTemplateId,
    createPointOfInterestTemplate,
    selectedType?.accentColor,
    selectedType?.fields,
    selectedType?.icon,
    selectedType?.name,
    selectedType?.type,
    selectedType?.blisterFieldsConfig,
    structureId,
  ]);

  const [deletePointOfInterestTemplate, { loading }] = useDeletePointOfInterestTemplateMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument],
    onCompleted: () => {
      setIsDeleteDialogOpen(false);
      showSnackBar('POI template has been deleted successfully', 'success');
    },
    onError: (error) => {
      setIsDeleteDialogOpen(false);
      showSnackBar(`${error.message}`, 'error');
    },
  });

  const handleDeleteTemplate = useCallback(() => {
    if (selectedPoiCount > 0) {
      setIsDeleteDialogOpen(false);
    } else {
      deletePointOfInterestTemplate({
        variables: {
          templateId: selectedType?.id ?? '',
        },
      });
    }
  }, [deletePointOfInterestTemplate, selectedPoiCount, selectedType?.id]);

  const deleteDialogButtonText = useMemo(() => {
    if (selectedPoiCount > 0) {
      return 'Got it';
    }
    return loading ? 'Deleting...' : 'Delete Template';
  }, [loading, selectedPoiCount]);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  };

  const tabs = useMemo(() => {
    return [
      {
        index: 0,
        label: 'Fields',
        component: (
          <PoiFieldsPanel
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
            handleCreateTemplate={handleCreateTemplate}
            handleSaveTemplate={handleSaveTemplate}
            setIsDeleteDialogOpen={setIsDeleteDialogOpen}
            allPoiIcons={allPoiIcons}
          />
        ),
      },
      {
        index: 1,
        label: 'Marker Label (Annotation)',
        component: (
          <PoiMarkerLabelPanel handleSaveTemplate={handleSaveTemplate} examplePoi={examplePoi} />
        ),
      },
      {
        index: 2,
        label: 'Blister Fields',
        component: <PoiBlisterFieldsPanel handleSaveTemplate={handleSaveTemplate} />,
      },
      ...(workInProgress
        ? [
          {
            index: 3,
            label: 'Template Engine Kit',
            component: <PoiTemplateEngineKitPanel />,
          },
        ]
        : []),
    ];
  }, [
    allPoiIcons,
    emptyForm,
    handleCreateTemplate,
    handleEmptyForm,
    handleSaveTemplate,
    examplePoi,
  ]);

  if (!selectedType) return <PageLoader />;

  return (
    <>
      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          title="Delete Template"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              {selectedPoiCount > 0
                ? `This template has ${selectedPoiCount} POIs associated with it. Please delete all associated POIs before deleting the template.`
                : 'Are you sure you want to delete this template?'}
            </Typography>
          }
          actions={
            <>
              {selectedPoiCount === 0 && (
                <Button
                  variant="text"
                  onClick={() => setIsDeleteDialogOpen(false)}
                  sx={textButtonStyle}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => handleDeleteTemplate()}
                sx={containedButtonStyle}
                disabled={loading && selectedPoiCount === 0}
              >
                {deleteDialogButtonText}
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      <BasicTabs
        tabs={tabs}
        selectedTab={selectedTab}
        changeTab={handleTabChange}
        tabsStyles={poiTabStyles}
        customSeperator={<FloatingLine />}
        renderingStrategy="eager"
      />
    </>
  );
};
